import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Tooltip } from '@mui/material';
import { Bar, BarChart, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts';
import { ErrorKind } from '../../services/insightsApi';

export const KIND_META: Record<ErrorKind, { label: string; color: string; help: string }> = {
  time_limit: {
    label: 'Timeout / time limit',
    color: '#ef6c00',
    help: 'The run or a model call took too long. Check the time limit in Reliability settings, or use a faster model.',
  },
  rate_limit: {
    label: 'Rate limit / quota',
    color: '#8e24aa',
    help: 'The provider refused calls because of request or token limits. Backup models and lower concurrency help.',
  },
  model_unavailable: {
    label: 'Model not available',
    color: '#5d4037',
    help: 'The model was not found, retired, or not offered in the region the call went to.',
  },
  invalid_output: {
    label: 'Invalid output',
    color: '#c62828',
    help: "The model's answer could not be parsed or didn't match the response format.",
  },
  auth: { label: 'Auth / permission', color: '#283593', help: 'A key, credential or permission was rejected.' },
  input_too_large: {
    label: 'Input too large',
    color: '#00838f',
    help: "The input was over the model's context length or the request size limit.",
  },
  content_policy: {
    label: 'Content policy / safety',
    color: '#ad1457',
    help: "The provider's safety or content filter blocked the request or the answer.",
  },
  bad_input: { label: 'Bad request / input', color: '#558b2f', help: 'The request itself was rejected, e.g. invalid arguments or a media URL that could not be fetched.' },
  network: {
    label: 'Provider / network error',
    color: '#1565c0',
    help: 'Server errors, overloaded providers and dropped connections. Usually temporary.',
  },
  other: { label: 'Other', color: '#757575', help: "Errors that don't match a known type." },
};

export function useReport<T>(load: () => Promise<T>): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { data, loading, error };
}

const withCustomer = (path: string, customerId?: string | null, params: Record<string, string> = {}) => {
  const q = new URLSearchParams(params);
  if (customerId) q.set('customer_id', customerId);
  const s = q.toString();
  return s ? `${path}?${s}` : path;
};

export const links = {
  functionPerformance: (versionId: string, customerId?: string | null) =>
    withCustomer(`/dashboard/function-runs/${versionId}`, customerId, { tab: 'performance' }),
  chainPerformance: (chainId: string, customerId?: string | null) => withCustomer(`/dashboard/chain-performance/${chainId}`, customerId),
  run: (type: 'function' | 'chain', id: string) => (type === 'chain' ? `/dashboard/chain-run/${id}` : `/dashboard/run/${id}`),
  learning: (type: 'function' | 'chain', id: string) => `/dashboard/learning/${type}/${id}`,
};

export const TargetLink: React.FC<{
  type: 'function' | 'chain' | 'tests';
  id: string | null;
  name: string;
  customerId?: string | null;
  children?: React.ReactNode;
}> = ({ type, id, name, customerId, children }) => {
  if (!id) return <>{children ?? name}</>;
  const to = type === 'chain' ? links.chainPerformance(id, customerId) : links.functionPerformance(id, customerId);
  return (
    <Tooltip title={`Open ${type === 'chain' ? 'chain' : 'function'} performance`}>
      <Link component={RouterLink} to={to} underline="hover">
        {children ?? name}
      </Link>
    </Tooltip>
  );
};

export const fmtCredits = (n?: number | null) => {
  if (n === undefined || n === null) return '–';
  if (n > 0 && n < 0.01) return n.toFixed(4);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtChange = (share?: number | null) => {
  if (share === undefined || share === null) return '–';
  const pct = Math.round(share * 1000) / 10;
  return `${pct > 0 ? '+' : ''}${pct}%`;
};

export const ago = (iso?: string | null) => {
  if (!iso) return '–';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
};

export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '–';

export const Sparkline: React.FC<{ values: number[]; labels: string[]; color: string; unit?: string; width?: number; height?: number }> = ({
  values,
  labels,
  color,
  unit = 'runs',
  width = 140,
  height = 32,
}) => {
  const data = values.map((v, i) => ({ v, label: labels[i] }));
  return (
    <Box sx={{ width, height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }} barCategoryGap={1}>
          <ChartTooltip
            cursor={{ fill: 'rgba(0,0,0,0.06)' }}
            wrapperStyle={{ fontSize: 11 }}
            labelFormatter={(_, payload) => (payload?.[0]?.payload as { label?: string } | undefined)?.label ?? ''}
            formatter={(v: number) => [v, unit]}
          />
          <Bar dataKey="v" fill={color} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
